import React from 'react';
import { Box } from '@mui/material';
import { Media } from '@askporter/utils';
import { GalleryVideo } from './GalleryVideo';

export interface GalleryItemProps {
  /**
   * The media file to display
   */
  file: Media;
  /**
   * Width of the item
   */
  width: number;
  /**
   * Height of the item
   */
  height: number;
  /**
   * Whether clicking opens preview (false when showing "+N more" overlay)
   */
  canPreview?: boolean;
  /**
   * Callback when item is clicked for preview
   */
  onPreview?: () => void;
}

/**
 * GalleryItem displays a single image or video in the gallery
 * Supports both image and video file types
 * Note: canPreview is only false when showing "+N more" overlay on the last item
 */
export const GalleryItem: React.FC<GalleryItemProps> = ({ file, width, height, canPreview = true, onPreview }) => {
  const mediaSrc = file.path || file.tempPath;
  const isVideo = file.nameAndType?.type === 'mp4' || file.nameAndType?.type === 'hevc';
  const mediaType = isVideo ? 'video' : 'image';
  const fileName = file.nameAndType?.name || `Gallery ${mediaType}`;
  const ariaLabel = `${fileName} - click to preview`;

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      sx={{
        position: 'relative',
        width,
        height,
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: 'pointer',
        backgroundColor: '#000',
      }}
      onClick={canPreview ? onPreview : undefined}
      onKeyDown={
        canPreview
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onPreview?.();
              }
            }
          : undefined
      }
    >
      {isVideo ? (
        <GalleryVideo src={mediaSrc || ''} alt={fileName} objectFit="cover" />
      ) : (
        <img
          src={mediaSrc || ''}
          alt={fileName}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      )}
    </Box>
  );
};
